/**
 * ResultsView — post-submission summary: overall score, pillar breakdown,
 * AI-generated KM recommendations, and a share-via-email CTA. Rendered both
 * after a fresh submit (results view branch) and when browsing a historical
 * submission from the history view.
 *
 * Parent is responsible for:
 *   - onShareResults(submission): open the share dialog with the submission
 *     pre-filled
 *   - onRefreshRecs(submissionId): re-trigger the async recommendations job
 *     (shown on error/loading states)
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, Sparkles } from 'lucide-react';
import { format } from 'date-fns';

export const ResultsView = ({ submission, onShareResults, onRefreshRecs }) => {
  const bd = submission.breakdown || {};
  const recs = submission.recommendations;
  const maturityColors = {
    Advanced: 'bg-[#34C759] text-white',
    Intermediate: 'bg-[#007AFF] text-white',
    Developing: 'bg-[#FF9500] text-white',
    Initial: 'bg-[#FF3B30] text-white',
  };
  const typeIcons = {
    people: { color: '#002FA7', label: 'People' },
    process: { color: '#FF9500', label: 'Process' },
    technology: { color: '#34C759', label: 'Technology' },
  };
  const timeColors = { '1-2 weeks': '#34C759', '2-4 weeks': '#007AFF', '1-3 months': '#FF9500' };

  return (
    <div className="space-y-6" data-testid="results-view">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#002FA7]/10 mb-4">
          <span className="text-3xl font-bold text-[#002FA7]">{submission.overall_score}%</span>
        </div>
        <h3 className="text-xl font-semibold text-[#0A0A0A]">Assessment Complete</h3>
        <Badge className={`mt-2 text-sm px-3 py-1 ${maturityColors[submission.maturity_level] || 'bg-[#9CA3AF]'}`}>
          {submission.maturity_level} Maturity
        </Badge>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {bd.quick_km && (
          <Card className="border-[#E5E7EB]"><CardContent className="p-4 text-center">
            <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">Process</p>
            <p className="text-2xl font-bold text-[#002FA7]">{bd.quick_km.pct}%</p>
            <p className="text-xs text-[#4B5563]">{bd.quick_km.score}/{bd.quick_km.max} points</p>
          </CardContent></Card>
        )}
        {bd.checklist && (
          <Card className="border-[#E5E7EB]"><CardContent className="p-4 text-center">
            <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">People</p>
            <p className="text-2xl font-bold text-[#34C759]">{bd.checklist.pct}%</p>
            <p className="text-xs text-[#4B5563]">{bd.checklist.yes}/{bd.checklist.total} practices</p>
          </CardContent></Card>
        )}
        {bd.technology && (
          <Card className="border-[#E5E7EB]"><CardContent className="p-4 text-center">
            <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">Technology</p>
            <p className="text-2xl font-bold text-[#FF9500]">{bd.technology.pct}%</p>
            <p className="text-xs text-[#4B5563]">{bd.technology.score}/{bd.technology.max} points</p>
          </CardContent></Card>
        )}
      </div>

      {/* KM Recommendations Section */}
      {recs && !recs.error && recs.areas ? (
        <div className="space-y-4" data-testid="recommendations-section">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-[#0A0A0A] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#002FA7]" />KM Improvement Recommendations
            </h3>
            <Badge variant="outline" className="text-[9px]">People → Process → Technology</Badge>
          </div>

          {/* Maturity Summary */}
          {recs.maturity_summary && (
            <div className="p-3 rounded-lg bg-[#002FA7]/[0.03] border border-[#002FA7]/15" data-testid="maturity-summary">
              <p className="text-xs text-[#4B5563] leading-relaxed">{recs.maturity_summary}</p>
              {recs.primary_focus && (
                <p className="text-xs font-semibold text-[#002FA7] mt-2">Primary focus: {recs.primary_focus}</p>
              )}
            </div>
          )}

          {/* Recommendations by Area */}
          {recs.areas.map((area, aIdx) => (
            <Card key={aIdx} className="border-[#E5E7EB]" data-testid={`rec-area-${aIdx}`}>
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider">
                  {area.area}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 pt-0">
                <div className="space-y-2">
                  {area.recommendations?.map((rec, rIdx) => {
                    const t = typeIcons[rec.type] || typeIcons.people;
                    return (
                      <div
                        key={rIdx}
                        className="p-2.5 rounded-lg border-l-[3px] bg-[#FAFAFA] border border-[#E5E7EB]"
                        style={{ borderLeftColor: t.color }}
                        data-testid={`rec-${aIdx}-${rIdx}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <p className="text-[11px] text-[#0A0A0A] leading-relaxed">{rec.text}</p>
                            {rec.pain_point && (
                              <p className="text-[9px] text-[#9CA3AF] mt-1 italic">Addresses: {rec.pain_point}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                            <Badge className="text-[7px] px-1.5 py-0" style={{ backgroundColor: t.color + '12', color: t.color }}>{t.label}</Badge>
                            {rec.timeframe && (
                              <Badge
                                variant="outline"
                                className="text-[7px] px-1 py-0"
                                style={{ borderColor: (timeColors[rec.timeframe] || '#9CA3AF') + '40', color: timeColors[rec.timeframe] || '#9CA3AF' }}
                              >
                                {rec.timeframe}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : recs?.error ? (
        <div className="p-4 rounded-lg bg-[#FF9500]/5 border border-[#FF9500]/20 text-center" data-testid="recs-error">
          <p className="text-xs text-[#FF9500] mb-2">Recommendations could not be generated</p>
          {onRefreshRecs && (
            <Button size="sm" variant="outline" className="text-xs" onClick={() => onRefreshRecs(submission.submission_id)} data-testid="retry-recs-btn">
              <Sparkles className="w-3 h-3 mr-1" />Retry
            </Button>
          )}
        </div>
      ) : recs === null ? (
        <div className="p-4 rounded-lg bg-[#002FA7]/5 border border-[#002FA7]/15 text-center" data-testid="recs-loading">
          <Loader2 className="w-5 h-5 animate-spin text-[#002FA7] mx-auto mb-2" />
          <p className="text-xs text-[#4B5563]">Generating personalized KM recommendations...</p>
          {onRefreshRecs && (
            <Button size="sm" variant="outline" className="text-xs mt-2" onClick={() => onRefreshRecs(submission.submission_id)} data-testid="refresh-recs-btn">
              <Sparkles className="w-3 h-3 mr-1" />Refresh
            </Button>
          )}
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <p className="text-xs text-[#9CA3AF]">
          Submitted by {submission.user_name} on {format(new Date(submission.created_at), 'MMM d, yyyy h:mm a')}
        </p>
        {onShareResults && (
          <Button
            size="sm"
            variant="outline"
            className="text-xs text-[#002FA7] border-[#002FA7]/30"
            onClick={() => onShareResults(submission)}
            data-testid="share-results-btn"
          >
            <Mail className="w-3 h-3 mr-1" />Share Results
          </Button>
        )}
      </div>
    </div>
  );
};
